import React from 'react';
import { Link } from 'react-router-dom';
import PageBanner from '../components/PageBanner';
import CTASection from '../components/CTASection';
import { ArrowRight } from 'lucide-react';

const FOUNDER = {
  name: 'Her Excellency Moleboheng Matli',
  role: 'PRESIDENT & CHAIRPERSON',
  image:
    'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/p9519khq_4.jpg',
  slogan: '\u201CGenerosity doesn\u2019t come from wealth, it comes from the heart\u201D',
  bio: [
    'Moleboheng Matli is a South African leader, development strategist, and social entrepreneur serving as the Founder, President, and Executive Chairperson of the Moleboheng Matli Foundation (MMF), a social impact organisation focused on education, literacy enhancement, youth empowerment, leadership development, and community transformation across Africa.',
    'She holds a Master\u2019s in Business Leadership (MBL) and is currently a PhD Candidate, with her academic and professional focus centred on leadership, development systems, education, and social impact innovation. Her work integrates academic insight with practical implementation across education, governance, and community development initiatives.',
    'Through MMF, she leads flagship programmes including the Moleboheng Matli E-Learning Centre & Library, youth mentorship and leadership development initiatives, and women and community empowerment programmes. These initiatives are designed to expand access to education, strengthen human capital development, and support long-term socio-economic transformation.',
    'Her leadership extends beyond organisational management into convening and shaping multi-stakeholder platforms that connect education, policy dialogue, investment engagement, and cross-border collaboration. This positions MMF within a broader ecosystem of African development, partnerships, and institutional cooperation.',
    'Moleboheng Matli\u2019s work is grounded in the belief that education is the most powerful tool for transformation, and that sustainable development is achieved through access, opportunity, and intentional leadership development that empowers individuals and communities to reach their full potential.',
    'She has also been recognised for her contributions in governance, media, philanthropy, and business leadership, with a focus on advancing inclusive growth, women\u2019s empowerment, and youth development across South Africa and the continent.',
    'Under her leadership, MMF continues to grow as a catalyst for structured, values-driven, and impact-oriented development work that bridges education, leadership, and sustainable community transformation.',
  ],
};

// 2nd-tier: Executive committee
const EXECUTIVES = [
  {
    name: 'Meagan Chauke Adonis',
    role: 'Head of Communications',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/yo4ofiuh_image.png',
  },
  {
    name: 'Felicia Malebe',
    role: 'Public Relations Officer',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/mvdjpzey_image.png',
  },
  {
    name: 'Gearges Macnobson Idowu',
    role: 'Head of Operations Nigeria',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/n6mxpym3_image.png',
  },
];

// 3rd-tier: Support Team
const LEADS = [
  {
    name: 'Wisdom Ijeoma',
    role: 'Support',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/ehhyo4jz_image.png',
  },
  {
    name: 'Ijeoma Christiana Egwuiyi',
    role: 'Administrative Support',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/6uy23ekk_image.png',
  },
];

// 4th-tier: Media Team
const MEDIA_TEAM = [
  {
    name: 'Chigozie Geness Iferobia',
    role: 'Graphic Designer',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/zjziuhk6_image.png',
  },
  {
    name: 'Benson Mruma',
    role: 'Photographer',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/a873op5l_image.png',
  },
  {
    name: 'Damilola Adeyison',
    role: 'Director of Photography (DOP)',
    image:
      'https://customer-assets.emergentagent.com/job_site-architecture-1/artifacts/7gvo94sm_image.png',
  },
];

const MemberCard = ({ m, size = 'md' }) => {
  const dims =
    size === 'lg'
      ? 'w-44 h-44 md:w-52 md:h-52'
      : size === 'sm'
      ? 'w-28 h-28 md:w-32 md:h-32'
      : 'w-36 h-36 md:w-40 md:h-40';
  return (
    <div className="flex flex-col items-center text-center">
      <div className={`${dims} rounded-full overflow-hidden shadow-lg ring-4 ring-white border border-slate-100 card-lift`}>
        <img src={m.image} alt={m.name} className="w-full h-full object-cover" />
      </div>
      <h4 className="heading-display mt-4 text-base md:text-lg font-bold text-slate-900 leading-tight max-w-[200px]">
        {m.name}
      </h4>
      <p className="text-[11px] md:text-xs font-semibold tracking-[0.18em] uppercase text-[#F97559] mt-1">
        {m.role}
      </p>
    </div>
  );
};

const FoundingTeam = () => (
  <main>
    <PageBanner title="Founding Team" crumb="Founding Team" />

    {/* Founder hero */}
    <section className="py-20 md:py-24 bg-white">
      <div className="max-w-[1200px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
        <div className="lg:col-span-2 relative">
          <div className="relative h-[480px] md:h-[560px] overflow-hidden rounded-sm shadow-2xl">
            <img src={FOUNDER.image} alt={FOUNDER.name} className="w-full h-full object-cover" />
          </div>
          <div className="absolute -bottom-5 -left-5 w-24 h-24 bg-[#F97559] text-white grid place-items-center heading-display text-5xl font-bold shadow-xl">
            M
          </div>
          <div className="absolute top-4 right-4 flex flex-col gap-1">
            <span className="w-1.5 h-10 bg-[#F97559] inline-block" />
            <span className="w-1.5 h-10 bg-[#2BBECF] inline-block ml-2" />
          </div>
        </div>

        <div className="lg:col-span-3">
          <p className="uppercase text-xs tracking-[0.3em] font-bold text-[#C9A23E] mb-3">
            Meet our Founder
          </p>
          <h2 className="heading-display text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 leading-tight">
            {FOUNDER.name}
          </h2>
          <p className="mt-2 text-sm font-semibold tracking-[0.2em] uppercase text-[#F97559]">
            {FOUNDER.role}
          </p>
          <div className="mt-5 flex items-center gap-1">
            <span className="w-12 h-0.5 bg-[#F97559]" />
            <span className="w-2 h-2 rotate-45 bg-[#F97559]" />
            <span className="w-12 h-0.5 bg-[#F97559]" />
          </div>
          <div className="mt-6 space-y-5 text-slate-600 leading-8 text-[15px]">
            {FOUNDER.bio.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          <blockquote className="mt-8 border-l-4 border-[#F97559] bg-slate-50 pl-6 pr-5 py-5 italic heading-display text-lg md:text-xl text-slate-800 leading-relaxed">
            {FOUNDER.slogan}
          </blockquote>
        </div>
      </div>
    </section>

    {/* Family tree */}
    <section className="py-20 md:py-24 bg-slate-50">
      <div className="max-w-[1300px] mx-auto px-6">
        <div className="text-center mb-14">
          <p className="uppercase text-xs tracking-[0.3em] font-bold text-[#F97559] mb-3">
            Organisational Tree
          </p>
          <h2 className="heading-display text-4xl md:text-5xl font-bold text-slate-900">
            Our Founding Team
          </h2>
          <div className="mx-auto mt-4 flex items-center justify-center gap-1">
            <span className="w-10 h-0.5 bg-[#F97559]" />
            <span className="w-2 h-2 rotate-45 bg-[#F97559]" />
            <span className="w-10 h-0.5 bg-[#F97559]" />
          </div>
        </div>

        {/* Tier 1 - Founder (large) */}
        <div className="flex flex-col items-center">
          <div className="flex flex-col items-center text-center">
            <div className="w-48 h-48 md:w-60 md:h-60 rounded-full overflow-hidden shadow-2xl ring-[6px] ring-white border-2 border-[#F97559] card-lift">
              <img src={FOUNDER.image} alt={FOUNDER.name} className="w-full h-full object-cover object-top" style={{ objectPosition: '50% 12%' }} />
            </div>
            <h3 className="heading-display mt-5 text-xl md:text-2xl font-bold text-slate-900">
              {FOUNDER.name}
            </h3>
            <p className="text-xs md:text-sm font-semibold tracking-[0.2em] uppercase text-[#F97559] mt-1">
              {FOUNDER.role}
            </p>
          </div>

          {/* Vertical connector down */}
          <div className="w-px h-10 bg-slate-300" />

          {/* Horizontal connector spanning executives */}
          <div className="hidden md:block relative w-full max-w-[820px] h-px bg-slate-300" />

          {/* Tier 2 - Executives */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-16 mt-0 md:mt-0 w-full max-w-[820px]">
            {EXECUTIVES.map((m, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="hidden md:block w-px h-10 bg-slate-300" />
                <div className="md:hidden w-px h-6 bg-slate-300" />
                <MemberCard m={m} size="md" />
              </div>
            ))}
          </div>

          {/* Connector to leads */}
          <div className="w-px h-10 bg-slate-300 mt-10" />

          {/* Support Team divider */}
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-0.5 bg-[#F97559]" />
            <p className="uppercase text-xs tracking-[0.3em] font-bold text-[#F97559]">
              Support Team
            </p>
            <span className="w-10 h-0.5 bg-[#F97559]" />
          </div>
          <div className="hidden md:block relative w-full max-w-[820px] h-px bg-slate-300 mt-6" />

          {/* Tier 3 - Support Team */}
          <div className="grid grid-cols-2 gap-8 md:gap-12 w-full max-w-[520px]">
            {LEADS.map((m, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="hidden md:block w-px h-8 bg-slate-300" />
                <div className="md:hidden w-px h-5 bg-slate-300" />
                <MemberCard m={m} size="sm" />
              </div>
            ))}
          </div>

          {/* Media Team divider */}
          <div className="w-px h-12 bg-slate-300 mt-12" />
          <div className="flex items-center gap-3 mb-2">
            <span className="w-10 h-0.5 bg-[#2BBECF]" />
            <p className="uppercase text-xs tracking-[0.3em] font-bold text-[#2BBECF]">
              Media Team
            </p>
            <span className="w-10 h-0.5 bg-[#2BBECF]" />
          </div>
          <div className="hidden md:block relative w-full max-w-[820px] h-px bg-slate-300 mt-6" />

          {/* Tier 4 - Media Team */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-8 md:gap-12 w-full max-w-[820px]">
            {MEDIA_TEAM.map((m, i) => (
              <div key={i} className="flex flex-col items-center">
                <div className="hidden md:block w-px h-8 bg-slate-300" />
                <div className="md:hidden w-px h-5 bg-slate-300" />
                <MemberCard m={m} size="sm" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* Join the team CTA */}
    <section className="py-20 md:py-24 bg-white">
      <div className="max-w-[1100px] mx-auto px-6 text-center">
        <h2 className="heading-display text-3xl md:text-4xl font-bold text-slate-900">
          Want to join our mission?
        </h2>
        <p className="mt-5 max-w-2xl mx-auto text-slate-600 leading-8 text-[15px]">
          We are always looking for passionate volunteers, mentors and board
          members to help us empower African girls and women through education.
        </p>
        <Link
          to="/contact"
          className="mt-8 inline-flex items-center gap-2 bg-[#F97559] hover:bg-[#e65a40] text-white font-semibold px-7 py-3.5 rounded-sm transition-colors"
        >
          Get in touch <ArrowRight size={16} />
        </Link>
      </div>
    </section>

    <CTASection />
  </main>
);

export default FoundingTeam;
